import React, { useMemo } from 'react';
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { SelectOption, SelectProps } from './types';
import { IconSVG, Spinner } from '@ui';
import { Select as SelectAntD } from 'antd';
import { ReactComponent as CloseIcon } from 'resources/icons/remix-icons/close-line.svg';

/**
 * The `Select` component renders a dropdown menu of selectable options.
 * It can display a single choice or support multiple selections.
 * Options can be dynamically loaded, and a loading state is available
 * to indicate data fetching. A search function is also included to
 * filter options by their labels.
 *
 * @param {SelectProps} props - The properties defined in `SelectProps`.
 */
function Select<Option = unknown>({
  value,
  options,
  placeholder,
  disabled,
  size = 'large',
  onChange,
  onBlur,
  onSelect,
  mode,
  loading,
  allowClear = true,
  excludeOptions,
  ...rest
}: SelectProps<Option>) {
  const { t } = useTranslation('form');

  const modifiedValue = useMemo(() => {
    if (!value || !options || !options.length) {
      return undefined;
    }

    return value;
  }, [value, options, loading]);

  const fieldPlaceholder = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }

    if (mode === 'multiple') {
      return t('field_placeholders.multi_select_optional');
    } else {
      return t('field_placeholders.select_optional');
    }
  }, [mode, placeholder, t]);

  const modifiedOptions = useMemo(() => {
    if (!options?.length) {
      return [];
    }

    let result = [...options];

    if (excludeOptions?.length) {
      result = result.filter((e) => !excludeOptions.some((el) => el === e.id));
    }

    return result;
  }, [options, excludeOptions]);

  const renderOptions = (items: SelectOption<Option>[]) => {
    return items.reduce<React.ReactNode[]>((acc, next) => {
      if (!next.hidden) {
        acc.push(
          <StyledSelectAntD.Option
            key={next.id}
            value={next.id}
            model={next.model}
            disabled={next.disabled}
          >
            {next.label}
          </StyledSelectAntD.Option>,
        );
      }

      return acc;
    }, []);
  };

  return (
    <StyledSelectAntD
      {...rest}
      allowClear={allowClear}
      showArrow
      clearIcon={
        <StyledIconSVG
          component={CloseIcon}
          color={colorsTheme.colorWhite}
          size="extra-small"
        />
      }
      mode={mode}
      size={size}
      value={modifiedValue}
      onBlur={onBlur}
      loading={loading}
      disabled={disabled || loading}
      onChange={onChange as any}
      onSelect={onSelect as any}
      placeholder={fieldPlaceholder}
      showSearch
      optionFilterProp="children"
      filterOption={(input: string, option: any) =>
        option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >=
        0
      }
      notFoundContent={
        loading ? (
          <LoaderWrapper>
            <Spinner />
          </LoaderWrapper>
        ) : undefined
      }
    >
      {renderOptions(modifiedOptions)}
    </StyledSelectAntD>
  );
}

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledSelectAntD = styled(SelectAntD)`
  .ant-select-clear {
    background-color: ${({ theme }) => theme.colorWarning} !important;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50px;
    svg {
      transform: none;
      position: unset;
    }
  }
`;

const StyledIconSVG = styled(IconSVG)`
  cursor: pointer;
`;

export default Select;
